import { createFileRoute } from "@tanstack/react-router";
import { serviceSchemaScripts } from "@/lib/service-schema";
import { freshnessMeta, webPageSchemaScript } from "@/lib/page-freshness";
import { FaqSection, faqSchemaScript, type FaqItem } from "@/components/landing/FaqSection";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { ParticleBackground } from "@/components/landing/ParticleBackground";
import { PageIntro } from "@/components/landing/PageIntro";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { ClosingCTA } from "@/components/landing/ClosingCTA";
import { RelatedServices } from "@/components/landing/RelatedServices";
import { ArrowUpRight, Check } from "lucide-react";
import { LaptopVideo } from "@/components/landing/LaptopVideo";
import acreAssessmentAsset from "@/assets/acre-assessment-mockup.png.asset.json";
import campaignVideoAsset from "@/assets/campaign-framework-builder.webm.asset.json";
import campaignPosterAsset from "@/assets/campaign-framework-builder-poster.jpg.asset.json";

const PUBLISHED = "2026-09-23T00:00:00+00:00";
const MODIFIED = "2026-10-03T00:00:00+00:00";

const TITLE = "Assessments, Turn Your Expertise into Qualified Leads | Square Migration";
const DESCRIPTION =
  "Custom interactive assessments that score your prospects against your real criteria, deliver an instant personalized result, and hand your sales team a qualified, context-rich lead.";

const BOOM_FEATURES = [
  "Website Scan & AI Copy",
  "Multi-Platform Ad Mock-Ups",
  "Budget Recommendations",
];

const ASSESSMENT_FEATURES = [
  "Learning environment score",
  "Room-by-room layout insights",
  "Prioritized improvement recommendations",
  "Clear path to expert guidance",
];

const FAQS: FaqItem[] = [
  {
    question: "What is an assessment tool for lead generation?",
    answer:
      "It's an interactive tool on your website that evaluates a visitor against criteria that matter to your business, readiness, maturity, fit, risk, and gives them a personalized score or diagnosis. The visitor gets a real answer about their own situation, and you get a lead who has already told you how qualified they are.",
  },
  {
    question: "How do assessments generate better leads than forms?",
    answer:
      "Every submission arrives with context: the prospect's answers, their score, and where they sit against your criteria. Your sales team opens the follow-up conversation knowing exactly what the prospect is dealing with, instead of working a bare name and email. Prospects who complete an assessment have also self-qualified by finishing it.",
  },
  {
    question: "What does a custom assessment cost to build?",
    answer:
      "It depends on the scoring logic and integrations, but most assessments ship in weeks, not quarters. Bring your evaluation criteria, even a rough checklist or spreadsheet, to a call and we'll scope it. You'll know the cost and timeline before anything is built.",
  },
  {
    question: "Where do the results and leads go?",
    answer:
      "Each completed assessment includes the visitor's answers, their score, and their contact details, routed to your CRM or inbox. High scores can trigger immediate sales follow-up while lower scores feed a nurture track, all with the full context of what the prospect told you.",
  },
];

export const Route = createFileRoute("/assessments")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://www.squaremigration.com/assessments" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://www.squaremigration.com/og-image.jpg" },
      { name: "twitter:image", content: "https://www.squaremigration.com/og-image.jpg" },
      ...freshnessMeta({ published: PUBLISHED, modified: MODIFIED }),
    ],
    links: [
      { rel: "canonical", href: "https://www.squaremigration.com/assessments" },
    ],
    scripts: [
      ...serviceSchemaScripts({
        path: "/assessments",
        name: "Interactive Assessments",
        description: DESCRIPTION,
        serviceType: "Interactive assessment tool development",
      }),
      faqSchemaScript(FAQS),
      webPageSchemaScript({
        path: "/assessments",
        name: TITLE,
        description: DESCRIPTION,
        published: PUBLISHED,
        modified: MODIFIED,
      }),
    ],
  }),
  component: AssessmentsPage,
});

function AssessmentsPage() {
  return (
    <main className="relative">
      <ParticleBackground />
      <Header />
      <PageIntro
        eyebrow="Assessments"
        title="Score your prospects before the first sales call."
        description="An interactive assessment turns your evaluation criteria into an experience: prospects answer questions about their own situation, get an instant personalized score, and land in your pipeline already qualified, with every answer attached."
        bullets={["Built for your domain", "Branded to your company", "Scores routed to your CRM", "Live in weeks, not quarters"]}
      />

      <section className="border-t border-border/50 bg-background/95 px-6 py-16 md:px-8 md:py-24 lg:px-12">
        <div className="mx-auto max-w-6xl">
          <div className="mb-12 grid items-end gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
            <div>
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.22em] text-primary">Featured Project</p>
              <h2 className="text-3xl font-semibold leading-tight sm:text-4xl">Campaign Framework <span className="text-primary">Builder</span></h2>
            </div>
            <p className="leading-relaxed text-muted-foreground lg:text-lg">An AI-guided builder for Boom Marketing that scans a prospect's website and returns ad copy, creative mock-ups and a budget plan across Facebook, Instagram, Google and LinkedIn. Prospects walk away with a real campaign framework, and Boom gets a qualified lead attached to it.</p>
          </div>
          <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
            <LaptopVideo
              src={campaignVideoAsset.url}
              poster={campaignPosterAsset.url}
              ariaLabel="Boom Marketing campaign framework builder displayed on a laptop"
            />
            <div>
              <p className="mb-6 text-lg leading-relaxed text-foreground/90">The builder takes a prospect from blank page to working campaign in one sitting: it reads their existing site, drafts copy in their voice, mocks up the creative for each platform, and recommends a budget to match. Every output is grounded in what the prospect already does, so the conversation that follows starts from real substance.</p>
              <ul className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                {BOOM_FEATURES.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary"><Check className="h-3.5 w-3.5" aria-hidden="true" /></span>
                    {item}
                  </li>
                ))}
              </ul>
              <a href="https://aim.ecommboom.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-primary/60 px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-primary/10">
                Try the Campaign Framework Builder <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-border/50 bg-background/95 px-6 py-16 md:px-8 md:py-24 lg:px-12">
        <div className="mx-auto max-w-6xl">
          <div className="mb-12 grid items-end gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
            <div>
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.22em] text-primary">Featured Project</p>
              <h2 className="text-3xl font-semibold leading-tight sm:text-4xl">Learning Environment <span className="text-primary">Assessment</span></h2>
            </div>
            <p className="leading-relaxed text-muted-foreground lg:text-lg">A guided assessment for ACRE that helps schools and districts evaluate the physical spaces where learning happens, from classroom layout to furniture, flow and flexibility, then shows them where small changes could make the biggest difference.</p>
          </div>
          <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
            <img src={acreAssessmentAsset.url} alt="ACRE Learning Environment Assessment displayed on a laptop" className="h-auto w-full object-contain" loading="lazy" />
            <div>
              <p className="mb-6 text-lg leading-relaxed text-foreground/90">The assessment focuses on the physical space itself: how classrooms are laid out, how furniture and fixtures support (or hinder) instruction, and how well each environment adapts to different ways of learning. Participants walk away with a clear picture of their space, and ACRE gets the context needed to recommend the right improvements.</p>
              <ul className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                {ASSESSMENT_FEATURES.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary"><Check className="h-3.5 w-3.5" aria-hidden="true" /></span>
                    {item}
                  </li>
                ))}
              </ul>
              <a href="https://educationassessment.acreworkplace.com/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-primary/60 px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-primary/10">
                Explore the Learning Environment Assessment <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <ProcessSection
        eyebrow="From Criteria to Qualified Leads"
        title="Here's how your assessment gets built."
        lead="Your assessment runs on your domain, branded to you, with scoring logic built from the criteria you already use to judge a good fit."
        steps={[
          { number: "01", title: "Pin the Criteria", description: "Name what separates a great-fit prospect from a poor one, readiness, maturity, budget, risk, in your own words." },
          { number: "02", title: "Build the Scoring", description: "We translate your criteria into questions, weights and benchmarks that produce a score prospects trust." },
          { number: "03", title: "Design & Ship", description: "Branded, mobile-friendly, and hosted on your domain, usually live within a few weeks." },
          { number: "04", title: "Route the Leads", description: "Scores, answers and contact details land in your CRM or inbox so follow-up starts with full context." },
        ]}
      />
      <ClosingCTA
        eyebrow="Build Your Assessment"
        headline={
          <>
            Ready to qualify leads <span className="text-primary">before the first call?</span>
          </>
        }
        subhead="Let's scope your assessment."
        body="Bring the criteria you already use to evaluate a prospect, a checklist, a scorecard, even instinct. In one call we'll tell you whether it makes a strong assessment and what it would take to build."
        ctaLabel="Schedule a Call"
      />
      <FaqSection items={FAQS} />
      <RelatedServices current="/assessments" />
      <Footer />
    </main>
  );
}
